class BagFinderQuiz extends HTMLElement {
  #currentStep = 0;
  #answers = {};
  #conversationHistory = [];
  #sessionId = null;
  #isSending = false;
  #selectedItems = new Set();

  #steps = [
    {
      key: 'sport',
      question: "What do you LOVE doing?",
      subtitle: "We'll find the perfect bag for your passions.",
      type: 'image-cards',
      options: [
        { value: 'Tennis', label: 'Tennis', imageKey: 'tennis' },
        { value: 'Padel', label: 'Padel', imageKey: 'padel' },
        { value: 'Pickleball', label: 'Pickleball', imageKey: 'pickleball' },
        { value: 'Adventure', label: 'Adventure', imageKey: 'adventure' },
      ],
      skipIf: null,
    },
    {
      key: 'racquets',
      question: "How many racquets do you carry?",
      type: 'text-cards',
      options: [
        { value: '1-2 Racquets', label: '1-2' },
        { value: '3-4 Racquets', label: '3-4' },
        { value: '5-6+ Racquets', label: '5-6+' },
      ],
      skipIf: (answers) => answers.sport === 'Adventure',
    },
    {
      key: 'items',
      question: "What does your bag need to fit?",
      subtitle: "Select as many as apply.",
      type: 'multi-image',
      options: [
        { value: 'Laptop', label: 'Laptop', imageKey: 'laptop' },
        { value: 'Tablet', label: 'Tablet', imageKey: 'tablet' },
        { value: 'Clothes', label: 'Clothes', imageKey: 'clothes' },
        { value: 'Shoes', label: 'Shoes', imageKey: 'shoes' },
        { value: 'Camera', label: 'Camera', imageKey: 'camera' },
        { value: 'Snacks', label: 'Snacks', imageKey: 'snacks' },
      ],
      skipIf: null,
    },
    {
      key: 'email',
      question: "You're almost done!",
      subtitle: "Drop your email for an exclusive gift with your first order.",
      type: 'email',
      skipIf: null,
    },
  ];

  connectedCallback() {
    this.apiEndpoint = this.dataset.apiEndpoint || 'https://cancha-ai-chat.cancha-chat.workers.dev/quiz';
    this.baseUrl = this.apiEndpoint.replace(/\/quiz$/, '');
    this.subscribeEndpoint = this.baseUrl + '/subscribe';
    this.config = this.#parseJson('[data-quiz-config]') || {};
    this.stageEl = this.querySelector('[data-stage]');
    this.progressBar = this.querySelector('[data-progress-bar]');
    this.progressText = this.querySelector('[data-progress-text]');
    this.backBtn = this.querySelector('[data-back-btn]');
    this.inputArea = this.querySelector('[data-input-area]');
    this.followUpForm = this.querySelector('[data-follow-up-form]');
    this.followUpInput = this.querySelector('[data-follow-up-input]');

    this.backBtn?.addEventListener('click', () => this.#goBack());
    this.followUpForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.followUpInput?.value?.trim();
      if (text) this.#handleFollowUp(text);
    });

    this.#track('quiz_started');
    this.#showStep(0);
  }

  #parseJson(selector) {
    const script = this.querySelector(`script${selector}`);
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch { return null; }
  }

  // ---- Navigation ----

  #getTotalSteps() {
    return this.#steps.filter((s) => !s.skipIf || !s.skipIf(this.#answers)).length;
  }

  #getVisibleStepIndex() {
    let count = 0;
    for (let i = 0; i < this.#currentStep; i++) {
      const step = this.#steps[i];
      if (!step.skipIf || !step.skipIf(this.#answers)) count++;
    }
    return count;
  }

  #updateProgress() {
    const total = this.#getTotalSteps();
    const current = this.#getVisibleStepIndex();
    const display = current + 1;
    const pct = Math.round((display / total) * 100);
    if (this.progressBar) this.progressBar.style.width = `${pct}%`;
    if (this.progressText) this.progressText.textContent = `${display} of ${total}`;
    if (this.backBtn) this.backBtn.hidden = current === 0;
  }

  #showStep(index, direction = 'forward') {
    this.#currentStep = index;

    // Skip steps whose condition is met
    const delta = direction === 'forward' ? 1 : -1;
    while (this.#currentStep >= 0 && this.#currentStep < this.#steps.length) {
      const step = this.#steps[this.#currentStep];
      if (step.skipIf && step.skipIf(this.#answers)) {
        this.#currentStep += delta;
        continue;
      }
      break;
    }

    this.#updateProgress();

    // Past last step — submit
    if (this.#currentStep >= this.#steps.length) {
      this.#submitQuiz();
      return;
    }
    // Before first step — clamp
    if (this.#currentStep < 0) {
      this.#currentStep = 0;
      return;
    }

    this.#renderSlide(this.#steps[this.#currentStep], direction);
  }

  #goBack() {
    if (this.#currentStep <= 0) return;
    const step = this.#steps[this.#currentStep];
    if (step) delete this.#answers[step.key];
    this.#showStep(this.#currentStep - 1, 'back');
  }

  #advance(key, value) {
    this.#answers[key] = value;
    const displayText = Array.isArray(value) ? value.join(', ') : value;
    this.#track('quiz_step_completed', { quiz_step: key, quiz_answer: displayText });
    this.#showStep(this.#currentStep + 1, 'forward');
  }

  // ---- Rendering ----

  #renderSlide(step, direction) {
    const slide = document.createElement('div');
    slide.className = 'bfq-slide';

    // Question
    const heading = document.createElement('h2');
    heading.className = 'bfq-slide__heading';
    heading.textContent = step.question;
    slide.appendChild(heading);

    if (step.subtitle) {
      const sub = document.createElement('p');
      sub.className = 'bfq-slide__subtitle';
      sub.textContent = step.subtitle;
      slide.appendChild(sub);
    }

    // Options
    switch (step.type) {
      case 'image-cards':
        slide.appendChild(this.#buildImageCards(step, 'sport_images'));
        break;
      case 'multi-image':
        this.#selectedItems.clear();
        slide.appendChild(this.#buildMultiImageCards(step));
        break;
      case 'text-cards':
        slide.appendChild(this.#buildTextCards(step));
        break;
      case 'email':
        slide.appendChild(this.#buildEmailCapture());
        break;
    }

    // Animate in
    this.#transitionSlide(slide, direction);
  }

  #transitionSlide(newSlide, direction) {
    const stage = this.stageEl;
    if (!stage) return;

    const oldSlide = stage.querySelector('.bfq-slide');
    if (!oldSlide) {
      stage.appendChild(newSlide);
      newSlide.classList.add('bfq-slide--enter');
      requestAnimationFrame(() => newSlide.classList.add('bfq-slide--active'));
      return;
    }

    // Slide out old
    const exitClass = direction === 'forward' ? 'bfq-slide--exit-left' : 'bfq-slide--exit-right';
    oldSlide.classList.add(exitClass);

    // Slide in new
    const enterClass = direction === 'forward' ? 'bfq-slide--enter-right' : 'bfq-slide--enter-left';
    newSlide.classList.add(enterClass);
    stage.appendChild(newSlide);

    requestAnimationFrame(() => {
      newSlide.classList.remove(enterClass);
      newSlide.classList.add('bfq-slide--active');
    });

    oldSlide.addEventListener('animationend', () => oldSlide.remove(), { once: true });
    // Fallback cleanup
    setTimeout(() => { if (oldSlide.parentNode) oldSlide.remove(); }, 400);
  }

  // ---- Option builders ----

  #buildImageCards(step, configKey) {
    const grid = document.createElement('div');
    grid.className = 'bfq-grid bfq-grid--2x2';

    step.options.forEach((opt) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'bfq-card bfq-card--image';

      const imgSrc = this.config[configKey]?.[opt.imageKey] || '';
      if (imgSrc) {
        const img = document.createElement('img');
        img.className = 'bfq-card__image';
        img.src = imgSrc;
        img.alt = opt.label;
        img.loading = 'lazy';
        card.appendChild(img);
      }

      const label = document.createElement('span');
      label.className = 'bfq-card__label';
      label.textContent = opt.label;
      card.appendChild(label);

      card.addEventListener('click', () => this.#advance(step.key, opt.value));
      grid.appendChild(card);
    });

    return grid;
  }

  #buildMultiImageCards(step) {
    const wrap = document.createElement('div');

    const grid = document.createElement('div');
    grid.className = 'bfq-grid bfq-grid--3col';

    step.options.forEach((opt) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'bfq-card bfq-card--image bfq-card--selectable';

      const imgSrc = this.config.item_images?.[opt.imageKey] || '';
      if (imgSrc) {
        const img = document.createElement('img');
        img.className = 'bfq-card__image';
        img.src = imgSrc;
        img.alt = opt.label;
        img.loading = 'lazy';
        card.appendChild(img);
      }

      const label = document.createElement('span');
      label.className = 'bfq-card__label';
      label.textContent = opt.label;
      card.appendChild(label);

      card.addEventListener('click', () => {
        if (this.#selectedItems.has(opt.value)) {
          this.#selectedItems.delete(opt.value);
          card.classList.remove('bfq-card--selected');
        } else {
          this.#selectedItems.add(opt.value);
          card.classList.add('bfq-card--selected');
        }
        continueBtn.disabled = this.#selectedItems.size === 0;
      });

      grid.appendChild(card);
    });

    wrap.appendChild(grid);

    const continueBtn = document.createElement('button');
    continueBtn.type = 'button';
    continueBtn.className = 'bfq-btn bfq-btn--primary';
    continueBtn.textContent = 'Continue';
    continueBtn.disabled = true;
    continueBtn.addEventListener('click', () => {
      this.#advance(step.key, Array.from(this.#selectedItems));
    });
    wrap.appendChild(continueBtn);

    return wrap;
  }

  #buildTextCards(step) {
    const grid = document.createElement('div');
    grid.className = 'bfq-grid bfq-grid--text';

    step.options.forEach((opt) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'bfq-card bfq-card--text';
      card.textContent = opt.label;
      card.addEventListener('click', () => this.#advance(step.key, opt.value));
      grid.appendChild(card);
    });

    return grid;
  }

  #buildEmailCapture() {
    const wrap = document.createElement('div');
    wrap.className = 'bfq-email';

    const form = document.createElement('form');
    form.className = 'bfq-email__form';

    const input = document.createElement('input');
    input.type = 'email';
    input.placeholder = 'Enter your email address';
    input.autocomplete = 'email';
    input.required = true;
    input.className = 'bfq-email__input';
    form.appendChild(input);

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'bfq-btn bfq-btn--primary';
    submitBtn.textContent = 'Submit';
    form.appendChild(submitBtn);

    wrap.appendChild(form);

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'bfq-email__skip';
    skipBtn.textContent = 'Skip — see my results';
    wrap.appendChild(skipBtn);

    // Prefill + inline error from a previous rejection (e.g. Kickbox said undeliverable)
    if (this.emailPrefill) {
      input.value = this.emailPrefill;
      this.emailPrefill = '';
    }
    if (this.emailErrorMessage) {
      const err = document.createElement('p');
      err.className = 'bfq-email__error';
      err.style.cssText = 'color:#c0392b;font-size:13px;margin-top:8px;text-align:center;';
      err.textContent = this.emailErrorMessage;
      wrap.insertBefore(err, skipBtn);
      this.emailErrorMessage = '';
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = input.value.trim();
      if (email) {
        this.#answers.email = email;
        this.#track('quiz_email_submitted');
        this.#advance('email', email);
      }
    });

    skipBtn.addEventListener('click', () => {
      this.#track('quiz_email_skipped');
      this.#answers.email = null;
      this.#advance('email', null);
    });

    setTimeout(() => input.focus(), 300);
    return wrap;
  }

  // Sent back here when the worker (Kickbox) hard-rejects the email.
  // We re-render the email step with the typed value prefilled and an inline error.
  #handleInvalidEmail(message) {
    this.emailErrorMessage = message || 'Please enter a valid email address.';
    this.emailPrefill = this.#answers.email || '';
    this.#answers.email = null;
    const idx = this.#steps.findIndex((s) => s.key === 'email');
    if (idx >= 0) {
      this.#showStep(idx, 'back');
      if (this.backBtn) this.backBtn.hidden = false;
    } else {
      this.#renderErrorSlide(this.emailErrorMessage);
    }
  }

  // ---- Submit & Results ----

  async #submitQuiz() {
    this.#updateProgress();

    // Show loading slide
    const slide = document.createElement('div');
    slide.className = 'bfq-slide bfq-slide--loading';

    const spinner = document.createElement('div');
    spinner.className = 'bfq-spinner';
    slide.appendChild(spinner);

    const loadingText = document.createElement('p');
    loadingText.className = 'bfq-slide__subtitle';
    loadingText.textContent = 'Finding your perfect match...';
    slide.appendChild(loadingText);

    this.#transitionSlide(slide, 'forward');
    if (this.backBtn) this.backBtn.hidden = true;

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: {
            sport: this.#answers.sport,
            racquets: this.#answers.racquets,
            items: this.#answers.items,
            gender: this.#answers.gender,
            age: this.#answers.age,
          },
          email: this.#answers.email || null,
          session_id: this.#sessionId ||= crypto.randomUUID(),
          commerce_context: { address_country: this.dataset.country, currency: this.dataset.currency, language: document.documentElement.lang || 'en' },
        }),
      });

      // Always parse the body so we can read structured errors like { error_code: 'invalid_email' }
      let data = null;
      try { data = await response.json(); } catch {}

      if (!response.ok) {
        if (response.status === 400 && data?.error_code === 'invalid_email') {
          this.#track('quiz_email_rejected', { reason: data.reason || '' });
          this.#handleInvalidEmail(data.error);
          return;
        }
        throw new Error('Quiz API error');
      }

      this.#sessionId = data.session_id;

      if (data.recommendations?.length > 0) {
        this.#track('quiz_completed', {
          quiz_recommended: data.recommendations.map((r) => r.handle).join(','),
          quiz_fallback: !!data.fallback,
          quiz_sport: this.#answers.sport,
        });
        this.#renderResultSlide(data.recommendations, !data.fallback);
      } else {
        this.#renderErrorSlide("Sorry, I couldn't generate recommendations right now. Please try again.");
      }

      // Complete progress bar
      if (this.progressBar) this.progressBar.style.width = '100%';
      const total = this.#getTotalSteps();
      if (this.progressText) this.progressText.textContent = `${total} of ${total}`;
    } catch (err) {
      console.error('Quiz error:', err);
      this.#renderErrorSlide("Something went wrong. Please refresh and try again.");
    }
  }

  #renderResultSlide(recommendations, showFollowUp) {
    const slide = document.createElement('div');
    slide.className = 'bfq-slide bfq-slide--result';

    const heading = document.createElement('h2');
    heading.className = 'bfq-slide__heading';
    heading.textContent = "Here's your perfect match";
    slide.appendChild(heading);

    recommendations.forEach((rec) => {
      slide.appendChild(this.#buildProductCard(rec));
    });

    if (showFollowUp) {
      const answerSummary = this.#buildAnswerSummary();
      this.#conversationHistory.push({ role: 'user', content: answerSummary });
      this.#conversationHistory.push({ role: 'assistant', content: JSON.stringify({ recommendations }) });
      if (this.inputArea) this.inputArea.hidden = false;
    }

    this.#transitionSlide(slide, 'forward');
  }

  #renderErrorSlide(message) {
    const slide = document.createElement('div');
    slide.className = 'bfq-slide';

    const heading = document.createElement('h2');
    heading.className = 'bfq-slide__heading';
    heading.textContent = message;
    slide.appendChild(heading);

    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.className = 'bfq-btn bfq-btn--primary';
    retryBtn.textContent = 'Try Again';
    retryBtn.addEventListener('click', () => location.reload());
    slide.appendChild(retryBtn);

    this.#transitionSlide(slide, 'forward');
  }

  #buildProductCard(rec) {
    const card = document.createElement('div');
    card.className = 'bfq-product';

    const catalogProduct = this.config.products?.[rec.handle];
    const allVariants = Array.isArray(rec.variants) ? rec.variants.map(v => ({
      id: v.variant_id?.split('/').at(-1), title: v.title, available: v.available === true,
      price: new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: v.price.currency }).format(v.price.amount),
      image: v.image_url,
    })) : [];
    const availableVariants = allVariants.filter((v) => v.available);
    const firstAvailable = availableVariants[0] || allVariants[0];
    const images = catalogProduct?.images || [];

    // Gallery
    const gallery = this.#buildGallery(images, catalogProduct?.title || rec.title || '');
    card.appendChild(gallery.container);

    const info = document.createElement('div');
    info.className = 'bfq-product__info';

    // Title
    const title = document.createElement('h3');
    title.className = 'bfq-product__title';
    title.textContent = catalogProduct?.title || rec.title || '';
    info.appendChild(title);

    // Price
    const priceEl = document.createElement('p');
    priceEl.className = 'bfq-product__price';
    this.#renderPrice(priceEl, firstAvailable);
    info.appendChild(priceEl);

    // Reason
    if (rec.reason) {
      const reason = document.createElement('p');
      reason.className = 'bfq-product__reason';
      reason.textContent = rec.reason;
      info.appendChild(reason);
    }

    // ATC
    let selectedVariant = firstAvailable;
    const atcBtn = document.createElement('button');
    atcBtn.type = 'button';
    atcBtn.className = 'bfq-btn bfq-btn--primary bfq-product__atc';
    atcBtn.textContent = selectedVariant?.available ? 'Add to Cart' : 'Sold Out';
    atcBtn.disabled = !selectedVariant?.available;
    atcBtn.addEventListener('click', () => {
      if (!selectedVariant?.available || !selectedVariant.id) return;
      this.#addToCart(atcBtn, selectedVariant.id);
    });

    // Variant selector
    if (availableVariants.length > 1) {
      const selectWrap = document.createElement('div');
      selectWrap.className = 'bfq-product__variants';
      const select = document.createElement('select');
      select.className = 'bfq-product__variant-select';
      availableVariants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.title;
        if (v === firstAvailable) opt.selected = true;
        select.appendChild(opt);
      });
      select.addEventListener('change', () => {
        selectedVariant = availableVariants.find((v) => String(v.id) === select.value) || firstAvailable;
        if (selectedVariant.image) {
          const idx = images.indexOf(selectedVariant.image);
          if (idx >= 0) gallery.goTo(idx);
        }
        this.#renderPrice(priceEl, selectedVariant);
        atcBtn.textContent = 'Add to Cart';
        atcBtn.disabled = false;
        atcBtn.classList.remove('bfq-btn--success');
      });
      selectWrap.appendChild(select);
      info.appendChild(selectWrap);
    }

    info.appendChild(atcBtn);

    // View product link
    const link = document.createElement('a');
    link.className = 'bfq-product__link';
    link.href = catalogProduct?.url || `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(rec.handle)}`;
    link.textContent = 'View product details';
    info.appendChild(link);

    card.appendChild(info);
    return card;
  }

  #buildGallery(images, alt) {
    const container = document.createElement('div');
    container.className = 'bfq-gallery';

    if (!images.length) {
      container.style.display = 'none';
      return { container, goTo() {} };
    }

    const track = document.createElement('div');
    track.className = 'bfq-gallery__track';

    images.forEach((src) => {
      const img = document.createElement('img');
      img.className = 'bfq-gallery__slide';
      img.src = src;
      img.alt = alt;
      img.loading = 'lazy';
      img.draggable = false;
      img.addEventListener('error', () => { img.style.display = 'none'; });
      track.appendChild(img);
    });

    container.appendChild(track);
    let current = 0;

    const goTo = (idx) => {
      current = Math.max(0, Math.min(idx, images.length - 1));
      track.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle('bfq-gallery__dot--active', i === current));
    };

    if (images.length > 1) {
      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'bfq-gallery__arrow bfq-gallery__arrow--prev';
      prevBtn.setAttribute('aria-label', 'Previous image');
      prevBtn.textContent = '\u2039';
      prevBtn.addEventListener('click', () => goTo(current - 1));
      container.appendChild(prevBtn);

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'bfq-gallery__arrow bfq-gallery__arrow--next';
      nextBtn.setAttribute('aria-label', 'Next image');
      nextBtn.textContent = '\u203A';
      nextBtn.addEventListener('click', () => goTo(current + 1));
      container.appendChild(nextBtn);
    }

    const dots = [];
    if (images.length > 1) {
      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'bfq-gallery__dots';
      images.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bfq-gallery__dot' + (i === 0 ? ' bfq-gallery__dot--active' : '');
        dot.setAttribute('aria-label', `Image ${i + 1}`);
        dot.dataset.index = i;
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
      dotsWrap.addEventListener('click', (e) => {
        const dot = e.target.closest('.bfq-gallery__dot');
        if (dot) goTo(Number(dot.dataset.index));
      });
      container.appendChild(dotsWrap);
    }

    // Touch swipe
    let startX = 0, deltaX = 0;
    container.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; deltaX = 0; }, { passive: true });
    container.addEventListener('touchmove', (e) => { deltaX = e.touches[0].clientX - startX; }, { passive: true });
    container.addEventListener('touchend', () => {
      if (deltaX > 40) goTo(current - 1);
      else if (deltaX < -40) goTo(current + 1);
    });

    return { container, goTo };
  }

  #renderPrice(el, variant) {
    el.textContent = '';
    if (!variant) return;
    if (variant.compare_at_price) {
      const strike = document.createElement('s');
      strike.textContent = variant.compare_at_price;
      el.append(strike, ' ', variant.price || '');
    } else {
      el.textContent = variant.price || '';
    }
  }

  #buildAnswerSummary() {
    const parts = [];
    if (this.#answers.sport) parts.push(`Sport: ${this.#answers.sport}`);
    if (this.#answers.racquets) parts.push(`Rackets: ${this.#answers.racquets}`);
    if (this.#answers.items?.length) parts.push(`Items: ${this.#answers.items.join(', ')}`);
    if (this.#answers.gender) parts.push(`Gender: ${this.#answers.gender}`);
    if (this.#answers.age) parts.push(`Age: ${this.#answers.age}`);
    return `Quiz answers:\n${parts.join('\n')}`;
  }

  // ---- Follow-up ----

  async #handleFollowUp(text) {
    if (this.#isSending) return;
    this.#isSending = true;
    this.followUpInput.value = '';
    this.#conversationHistory.push({ role: 'user', content: text });

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: {
            sport: this.#answers.sport,
            racquets: this.#answers.racquets,
            items: this.#answers.items,
            gender: this.#answers.gender,
            age: this.#answers.age,
          },
          follow_up: text,
          commerce_context: { address_country: this.dataset.country, currency: this.dataset.currency, language: document.documentElement.lang || 'en' },
          conversation_history: this.#conversationHistory,
          session_id: this.#sessionId,
        }),
      });

      if (!response.ok) throw new Error('Follow-up API error');
      const data = await response.json();
      this.#conversationHistory.push({ role: 'assistant', content: JSON.stringify({ recommendations: data.recommendations }) });

      if (data.recommendations?.length > 0) {
        this.#renderResultSlide(data.recommendations, false);
      }
    } catch (err) {
      console.error('Follow-up error:', err);
    } finally {
      this.#isSending = false;
      this.followUpInput?.focus();
    }
  }

  // ---- Add to Cart ----

  async #addToCart(btn, variantId) {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    let added = false;
    let addAttempted = false;
    try {
      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));

      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', '1');
      formData.set('properties[_cancha_source]', 'quiz');
      formData.set('properties[_cancha_assisted_at]', new Date().toISOString());
      formData.set('properties[_cancha_session_id]', this.#sessionId || '');
      if (sectionsToBundle.size > 0) {
        formData.set('sections', [...sectionsToBundle].join(','));
      }

      addAttempted = true;
      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        signal: AbortSignal.timeout(15000),
      });

      if (!addResponse.ok) { addAttempted = false; throw new Error('Add to cart failed'); }
      added = true;
      const responseJson = await addResponse.json();

      btn.textContent = 'Added!';
      btn.classList.add('bfq-btn--success');
      this.#track('quiz_add_to_cart', { variant_id: variantId });

      // Tag the cart with attribution source for order webhook tracking
      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: { '_cancha_source': 'quiz', '_cancha_assisted_at': new Date().toISOString(), '_cancha_session_id': this.#sessionId || '' } }),
      }).catch(() => {});

      // Track conversion server-side
      fetch(this.baseUrl + '/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'add_to_cart',
          event_id: crypto.randomUUID(),
          source: 'quiz',
          session_id: this.#sessionId || null,
          variant_id: String(variantId),
        }),
      }).catch(() => {});

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections || {};

      if (Object.keys(cartContent.sections).length === 0) {
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) {
          const sectionEl = drawerEl.closest('.shopify-section');
          const sectionId = sectionEl?.id?.replace('shopify-section-', '');
          if (sectionId) {
            cartContent.sections[sectionId] = await (await fetch(`${Shopify.routes.root}?section_id=${sectionId}`)).text();
          }
        }
      }

      const items = responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson];
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent, items },
      }));
    } catch (err) {
      console.error('ATC error:', err);
      if (added || addAttempted) {
        btn.textContent = added ? 'Added to cart' : 'Check your cart';
        const link = document.createElement('a');
        link.href = `${Shopify.routes.root}cart`;
        link.textContent = 'View cart';
        btn.after(link);
        return;
      }
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = 'Add to Cart'; btn.disabled = false; }, 2000);
    }
  }

  // ---- Analytics ----

  #track(event, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  }
}

if (!window.customElements.get('bag-finder-quiz')) {
  window.customElements.define('bag-finder-quiz', BagFinderQuiz);
}
